import type { EmployerSiteName } from './components/HirerPaymentTerms/hirerPaymentTermsTypes';
import type { Country } from './entities/Country';
import type { Currency } from './entities/Currency';
import type { PaymentMethodType, Environment, UseCase } from './entities/enums';
import type {
  Feature,
  HirerPaymentResult,
  Invoice,
  TrackingAdapter,
  InvoiceHirerPaymentResult,
} from './types';
import {
  AdBudgetView,
  CajaView,
  InvoiceView,
  OrderView,
  UpgradeView,
  GenericView,
} from './views';

export interface PaymentsForCajaProps {
  orderId: string;
  currency: Currency | string;
  onSuccess: (result: HirerPaymentResult) => void;
  onError: (error: any, paymentMethod?: PaymentMethodType) => void;
  feature?: Feature;
  invoiceOnly?: boolean;
  hideAllPayments?: boolean;
  // To remove after D1U
  advertiserNation?: Country; // backward compatible. should use currency in the future
  hideTabs?: boolean; // should be the same as invoiceOnly - keep it for the current ANZ tab layout
  // Hirer Payment Terms
  isAdBudgetSelected?: boolean;
  site: EmployerSiteName;
  env: Environment;
  // Preview Button
  previewButtonComponent?: JSX.Element;
  // Back Button
  backButtonComponent?: JSX.Element;
  postAdButtonText?: string;
  paymentMethod: PaymentMethodType;
  onPaymentMethodChanged?: (
    selectedIndex: number,
    selectedItem?: string,
  ) => void;
  onPostAdButtonClicked: ({
    paymentMethod,
    paymentMedium,
    isSavedCreditCard,
  }: {
    paymentMethod: PaymentMethodType;
    paymentMedium?: string;
    isSavedCreditCard?: boolean;
  }) => Promise<void>;
  onPostAdButtonClickFailed: (error: Error, isHandled?: boolean) => void;
  handledCancelPayment: () => void;
  onSavedCardLoaded?: () => void;
  onSaveCardOptionChanged?: (shouldSave: boolean) => void;
  onReplaceSavedCard?: () => void;
  idempotencyKey: string;
  interactionId?: string;
  billToAgent: boolean;
  trackingAdapter?: TrackingAdapter;
  trackLink?: (
    eventName: string,
    eventAttributes: Record<string, unknown>,
  ) => void;
  trackView?: (eventAttributes?: Record<string, unknown>) => void;
  cartId?: string;
  useCase: UseCase;
  onGetSurchargeInfo?: (
    surchargeBrand: string,
    surchargePercentage: number,
    surchargeApplicable: boolean,
  ) => void;
}

export interface PaymentsForAdBudgetProps extends PaymentsForCajaProps {
  useCase: 'ADBUDGET';
}

export interface PaymentsForUpgradeProps extends PaymentsForCajaProps {
  useCase: 'UPGRADE';
}

export interface PaymentsForOrderProps {
  orderId: string;
  currency: Currency;
  useCase: UseCase;
  cartId?: string;
  onSuccess: (result: HirerPaymentResult) => void;
  onError: (error: any, paymentMethod?: PaymentMethodType) => void;
  feature?: Feature;
  invoiceOnly?: boolean;
  hideAllPayments?: boolean;
  // Hirer Payment Terms
  isAdBudgetSelected?: boolean;
  site: EmployerSiteName;
  env: Environment;
  onPaymentMethodUpdated?: (selectedPaymentMethod: string) => void;
  handledCancelPayment: () => void;
  onSavedCardLoaded?: () => void;
  onSaveCardOptionChanged?: (shouldSave: boolean) => void;
  onReplaceSavedCard?: () => void;
  idempotencyKey: string;
  billToAgent: boolean;
  trackingAdapter?: TrackingAdapter;
  trackLink?: (
    eventName: string,
    eventAttributes: Record<string, unknown>,
  ) => void;
  children?: React.ReactNode;
  onGetSurchargeInfo?: (
    surchargeBrand: string,
    surchargePercentage: number,
    surchargeApplicable: boolean,
  ) => void;
}

// Payment Link Props
export interface PaymentsForPaymentLinkInvoiceProps {
  seekBillingAccountId: string;
  prn: string;
  email: string; // to send the receipt
  advertiserNation: Country;
  currency: Currency | string;
  env: Environment;
  site: EmployerSiteName;
  invoice: [Invoice];
  isPaymentLink: boolean;
  trackingAdapter?: TrackingAdapter;
  onSuccess: (result: InvoiceHirerPaymentResult) => void;
  onError: (error: any, paymentMethod?: PaymentMethodType) => void;
  onPostAdButtonClicked: () => Promise<void>;
  onPostAdButtonClickFailed: (error: Error, isHandled?: boolean) => void;
  onGetSurcharge: (result: {
    surchargeAmount: number;
    surchargeApplicable: boolean;
  }) => void; // for us to call ,function provided by BrandEx, when we can this, they will display the surcharge in UI and update the total
  defaultedPaymentMethod?: PaymentMethodType; // Only used for storybook. Do not use
  backButtonComponent?: JSX.Element;
}

// Non Payment Link Props
export interface PaymentsForNonPaymentLinkInvoiceProps
  extends PaymentsForPaymentLinkInvoiceProps {
  seekAdvertiserId: string; // map to customerId, customerNumber in BE
}

// Not to be used for consumers. We need to have an intersection type to be able to use the props in PaymentsForInvoice
export type PaymentsForInvoiceProps =
  | PaymentsForPaymentLinkInvoiceProps
  | PaymentsForNonPaymentLinkInvoiceProps;

// Not to be used for consumers. Union customerNumber and seekBillingAccountId to be able to use the props in InvoiceUseCase
// Exclude isPaymentLink since it's not required in InvoiceUseCase
// This type doesn't differentiate between payment link and non payment link
export type genericInvoiceProps = Omit<
  PaymentsForNonPaymentLinkInvoiceProps & {
    customerId: string;
    accountId: string;
  },
  'seekBillingAccountId' | 'seekAdvertiserId'
>;

export interface GenericPaymentsProps {
  env: Environment; // used to identify which stripe key to use
  country: Country; // This value is used to validate country for given seekPaymentIntentId
  site: string; // This value is used to validate site for given seekPaymentIntentId
  reference: string; // This value is used to validate reference for given seekPaymentIntentId
  seekPaymentIntentId: string; // used as unique key to identify payment intent
  onSuccess: (result: HirerPaymentResult) => void; // callback for onSuccess event
  onError: (error: Error) => void; // callback for onError event
}

const PaymentsForAdBudget = (props: PaymentsForAdBudgetProps) => (
  <AdBudgetView {...props} />
);

const PaymentsForCaja = (props: PaymentsForCajaProps) => (
  <CajaView {...props} />
);

const PaymentsForGeneric = (props: GenericPaymentsProps) => (
  <GenericView {...props} />
);

const PaymentsForInvoice = (props: PaymentsForInvoiceProps) => (
  <InvoiceView {...props} />
);

const PaymentsForOrder = (props: PaymentsForOrderProps) => (
  <OrderView {...props} internalChildren={() => null} />
);

const PaymentsForUpgrade = (props: PaymentsForUpgradeProps) => (
  <UpgradeView {...props} />
);

export {
  PaymentsForAdBudget,
  PaymentsForCaja,
  PaymentsForInvoice,
  PaymentsForOrder,
  PaymentsForUpgrade,
  PaymentsForGeneric,
};
